/**
 * Gestión de una montaña
 *
 * @link https://www.mendikat.net
 * @author Javi Urrutia <javi@mendikat.net>
 * @copyright 2023-2026 Mendikat
 * @license Mendikat
 *
 */

// Gestión de las ascensiones
import Climbs from '../climb/climb';

// Gestión de los documentos pdf subidos
import Documents from '../download/documents';

// Gestión de las modales comerciales
import Business from '../../common/business';

// Gestión de Google Adsense
import Adsense from '../../common/adsense';

// mobile-device-detect
// @link https://github.com/duskload/mobile-device-detect
import { isMobile } from 'mobile-device-detect';

// Leaftlet
import { LMap, LTileLayer, LControl, LMarker, LIcon, LPopup } from 'vue2-leaflet';
import { latLng } from 'leaflet';
import LControlFullscreen from 'vue2-leaflet-fullscreen';

// Estilos propios de Leaflet
import 'leaflet/dist/leaflet.css';
L.Icon.Default.prototype.options.imagePath = '/images/vendor/leaflet/dist/';

// VueCoolLightbox
// @link https://vue-cool-lightbox.lucaspulliese.com
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

// moment.js
// @link https://momentjs.com/
import moment from 'moment';

// numeral
// @link http://numeraljs.com/
import numeral from 'numeral';

// Vue Upload Component
// @author Lian Yue
// @link https://github.com/lian-yue/vue-upload-component
import VueUploadComponent from 'vue-upload-component';

import draggable from 'vuedraggable';

// Vuejs-Datepicker
// @link https://www.npmjs.com/package/vuejs-datepicker
import Datepicker from 'vuejs-datepicker';
import {ca, en, eu, es, fr, gl, pt} from 'vuejs-datepicker/src/locale';

import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

const { default: Axios } = require('axios');

const __MOUNT__ = window.__MOUNT__ || {};

const vm = new Vue({
    el: '#app',
    mixins: [Climbs, Documents, Business, Adsense],
    components: {
        LMap,
        LTileLayer,
        LControl,
        LMarker,
        LIcon,
        LPopup,
        LControlFullscreen,
        coollightbox : CoolLightBox,
        FileUpload   : VueUploadComponent,
        draggable,
        Datepicker,
        'v-select' : vSelect,
    },
    data:
        {
            // El idioma actual de la página
            lang : __MOUNT__.lang || 'es',

            // Idiomas del selector de fechas
            ca, en, eu, es, fr, gl, pt,

            // El modo en que se mostrando la información
            // Puede ser en modo visor o en modo edición para cambiar la información
            // 'view'|'edit'
            mode      : 'view',

            // Errores en la información
            errors    :
                {
                    name        : false,
                    location    : false,
                    altitude    : false,
                    prominence  : false,
                    utmZone     : false,
                    utmX        : false,
                    utmY        : false,
                    category    : false,
                    track       :
                        {
                            message : null,
                        }
                },

            // El usuario actual (si es un usuario registrado)
            user      : __MOUNT__.user || {},

            // El método (callback) a ejecutar cuando se selecciona un usuario
            // Un usuario puede ser seleccionado para añadirlo a los autores de la montaña,
            // para cambiar la autoría de una imagen
            userSelect: null,

            // La cumbre
            mountain  : __MOUNT__.mountain || null,

            // El datum actual
            datum     : 'WGS84',

            // La posición de la cumbre en coordenadas rectangulares (UTM o Lambert)
            position : null,

            // Las cumbres cercanas
            nearests  : __MOUNT__.nearests || [],

            // Los territorios
            regions   : __MOUNT__.regions || [],

            // El territorio actualmente seleccionado
            region    : null,

            // Los catálogos en los que está incluida la montaña
            catalogs  : __MOUNT__.catalogs || [],

            // El catálogo actualmente seleccionado
            catalog   : null,

            // El mapa
            map :
                {
                    object      : null,                                 // EL propio objeto
                    zoom        : 15,                                   // El nivel de zoom
                    center      : latLng(0, 0),                         // El centro del mapa
                    provider    : null,                                 // El proveedor de cartografía
                    summits     : [],                                   // Una lista de cumbres representadas
                },

            // Los proveedores de cartografía digital
            providers  : __MOUNT__.providers || [],

            // Orometría seleccionada
            orometry   :  __MOUNT__.orometry || '',

            // La lista de autores registrado
            authors     : __MOUNT__.authors || [],

            // La lista de imágenes
            images      : [],

            // El índice de la imagen mostrada o seleccionada
            image       : null,

            // Información de la imagen actualmente mostrada
            currentImage: null,

            // Búsqueda de usuarios
            searched :
                {
                    user     : null,        // El usuario buscado
                    users    : [],          // Los usuarios resultantes de la búsqueda
                },

            // La lista de nuevos archivos de imagen a subir
            files       : [],

            // La lista de tracks
            tracks      : [],

            // La lista de visores de cartografía
            viewers     : [],

            // El visor actualmente seleccionado
            viewer      : null,

            // El texto de filtro para los tracks
            filter      : null,

            // La lista de comentarios registrados
            posts       : [],

            // Opciones de subida de imágenes
            upload      :
                {
                    progress     : 0,                       // El grado de progreso de subida en tanto por ciento
                    maxFileSize  : 10 * 1024 * 1024,        // EL tamaño máximo de archivo admitido en bytes
                    imagesAcceptedTypes:                    // Los tipos de archivos de imagen aceptados
                        [
                            'image/jpeg',
                        ]
                },

            // La información de la autoría (información sobre el autor seleccionado)
            authorInfo : null,

            // Si la modal de información sobre el uso de la página debe ser mostrada o no
            howToUse: true,

            // Temporizador para que se cierre automáticamente una alerta
            dismiss:
                {
                    countDown : 0,      // Contador (tiempo) actual de la alerta
                    time      : 3,      // Tiempo para el cierre automático de la alerta
                },

            // Si se ha copiado el enlace a la url de la cumbre en el portapapeles
            linkCopied: false,

        },
    mounted: function () {

        // Si hay datos iniciales de la montaña, fija la posición
        if (this.mountain) {
            this.setMountain(
                this.mountain,
                __MOUNT__.positions,
                this.nearests
            );

            // Selecciona el primer territorio y catálogo
            this.region = this.regions[0];
            this.catalog = this.catalogs[0];

            // Prepara el centro y zoom del mapa antes de que se renderice
            this.map.zoom = parseInt(document.getElementById('map').dataset.zoomStart);
            this.map.center = latLng(this.mountain.latitude, this.mountain.longitude);

            // Carga los datos dinámicos (imágenes, posts, tracks, visores)
            this.loadDynamicData();
        }

        // Abre la modal con la información de funcionamiento de la página
        // útil para nuevos usuarios
        this.showHowToUseModalForNewUsers();
    },
    methods: {
        /**
         * Carga los datos dinámicos de la página (imágenes, posts, tracks, visores)
         *
         */
        loadDynamicData: function () {

            if (!this.mountain) return;

            Axios.get(`/com/mount/data/${this.mountain.id}`)
                .then(response => {

                    // Carga las imágenes
                    this.images = response.data.images || [];
                    this.images.forEach(image => this.completeImage(image));

                    // Carga los comentarios
                    this.posts = response.data.posts || [];
                    this.posts.forEach(post => post.editing = false);

                    // Carga los tracks
                    this.tracks = response.data.tracks || [];

                    // Carga los visores de cartografía
                    this.viewers = response.data.viewers || [];

                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Completa la información de una imagen
         *
         * @param {Image} image
         *
         */
        completeImage: function (image) {
            // Añade la ruta de la imagen
            image.src     = `/storage/mountains/${image.image}`;
            // Añade la ruta de la imagen en miniatura
            image.thumb   = `/storage/mountains/thumbs/${image.image}`;

            // La descripción (pié de la imagen) incluye el autor, el título y la fecha en la que ha sido obtenida o,
            // en su defecto, la fecha de creación
            image.description = `<a href="${image.fullUrl}">
                                    <i class="fas fa-camera fa-2x"></i></a>
                                 <span class="text-align-fa-2x ml-2">
                                    ${image.author ? image.author : '?'} :: ${moment(image.taken_at ? image.taken_at : image.created_at).format('DD-MM-YYYY')}
                                 </span>`;
        },
        /**
         * Fija lo información de la montaña
         *
         * @param {Object} mountain             La información de la montaña
         * @param {Array}  positions            Las posiciones de la montaña en diferentes datums
         * @param {Array}  nearest              Las montañas cercanas a la dada
         *
         */
        setMountain(mountain, positions, nearest) {

            this.mountain           = mountain;
            this.mountain.positions = positions;
            this.position           = this.mountain.positions[this.datum].utm.value;
            this.nearests           = nearest;
        },
        /**
         * Cuando el mapa está listo
         *
         * @param {Object} mapObject     El objeto del mapa
         */
        onMapReady: function (mapObject) {

            this.map.object = mapObject;

            // Obtiene el proveedor de cartografía a utilizar por defecto
            const defaultProvider = __MOUNT__.providers_default || 'CartoDB.Positron';
            this.map.provider = this.providers[defaultProvider];

            // Actualiza las cumbres en el mapa
            this.updateSummits();
        },
        /**
         * Obtiene los límites del mapa
         *
         * @returns {Bounds}        Los límites del mapa
         *
         */
        getMapBounds: function () {
            if (!this.map.object) return null;

            let bounds = this.map.object.getBounds();

            return {
                north : bounds.getNorth(),
                east  : bounds.getEast(),
                south : bounds.getSouth(),
                west  : bounds.getWest(),
            };
        },
        /**
         * Actualiza las cumbres en el mapa
         *
         */
        updateSummits: function () {

            if (!this.map.object) return;

            // Actualiza el nivel de zoom del mapa
            this.map.zoom = this.map.object.getZoom();

            // No se admite un nivel de zoom por encima del máximo para el proveedor de cartografía digital
            if (this.map.zoom > this.map.provider.options.maxZoom) {
                this.map.object.setZoom(this.map.provider.options.maxZoom);
                return;
            }

            // Obtiene los límites del mapa
            let bounds = this.getMapBounds();

            Axios.post('/com/map/get-mountains',
                {
                    bounds: bounds,
                }
            ).then(response => {
                this.map.summits = response.data;
            }).catch(error => {
                console.error(error);
            });
        },
        /**
         * Si la cumbre representada en el mapa se puede mover
         * con objeto de redefinir su posición
         *
         * @param {Summit} summit       La cumbre representada en el mapa
         *
         * @returns {Boolean}           Si la cumbre representada se puede mover
         */
        summitIsDraggable: function (summit) {
            return this.editable && summit.id == this.mountain.id;
        },
        /**
         * Actualiza la posición de la montaña
         *
         * @param {Event}  event    El evento
         * @param {Summit} summit   La montaña
         *
         */
        updateUtmPosition: function (event, summit) {

            summit.latitude  = event.lat;
            summit.longitude = event.lng;

            // Realiza la petición para obtener la posición completa partiendo de una posición en coordenadas geográficas
            Axios.post('/com/mount/position/geo',
                {
                    latitude    : event.lat,
                    longitude   : event.lng,
                }
            ).then(response => {

                // Obtiene la posición
                const position = response.data.position;

                this.mountain.utmZone   = position.zone;
                this.mountain.utmX      = position.x;
                this.mountain.utmY      = position.y;

                console.info(`Actualizada la posición de la cumbre: WGS84 ${this.mountain.utmZone} ${this.mountain.utmX} ${this.mountain.utmY}`);

                this.position = `${this.mountain.utmZone} ${this.mountain.utmX} ${this.mountain.utmY}`;

          }).catch(error => {
              console.error(error);
          });

        },
        /**
         * Al hacer click en el mapa se activa el uso de la rueda del ratón
         * para hacer zoom sobre el mismo
         *
         *
         */
        toggleScrollWheelZoom: function () {

            if (!this.map.object) return;

            if (this.map.object.scrollWheelZoom.enabled()) {
                this.map.object.scrollWheelZoom.disable();
            } else {
                this.map.object.scrollWheelZoom.enable();
            }
        },
        /**
         * Cambia el proveedor de cartografía digital
         *
         * @param {Event} event
         *
         */
        changeMapProvider: function (event) {
            // Obtiene el provvedor de cartografía seleccionado
            const selectedProvider = event.target.value;

            // Selecciona el proveedor de cartografía
            for (const provider in this.providers) {
                if (this.providers[provider].name == selectedProvider) {
                    this.map.provider = this.providers[provider];
                }
            }
        },
        /**
         * Cambia el datum
         *
         * @param {Event} event
         *
         */
        changeDatum: function (event) {
            this.position = this.mountain.positions[this.datum].utm.value;
        },
        /**
         * Abre el visor para mostrar una imagen de la galería
         *
         * @param {Number} image    El índice de la imagen
         *
         */
        showImage: function (image) {
            // Cambia la imagen
            this.image = image;

            // Se produce un alineamiento no deseado mientras carga la imagen
            // que curiosamente se resuelve al redimensionar la pantalla
            // Por ello, se fuerza el evento 'resize' con una demora que parece igualmente necesaria
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 250);
        },
        /**
         * Edita la siguiente imagen
         *
         */
        nextImage: function () {

            // Anula el contador de la alerta de guardado si estuviese activado
            this.dismiss.countDown = 0;

            // Obtiene el índice de la siguiente imagen
            let index = this.images.indexOf(this.currentImage);
            ++index;
            if (index ==  this.images.length) index = 0;

            this.currentImage = this.images[index];
        },
        /**
         * Edita la imagen previa
         *
         */
        previousImage: function () {

            // Anula el contador de la alerta de guardado si estuviese activado
            this.dismiss.countDown = 0;

            // Obtiene el índice de la siguiente imagen
            let index = this.images.indexOf(this.currentImage);
            --index;
            if (index == -1) index = this.images.length - 1;

            this.currentImage = this.images[index];
        },
        /**
         * Actualiza el contador para que una alerta desparezca
         *
         * @param {Number} dismissCountDown     El contador en segundos
         *
         */
        countDownChanged(dismissCountDown) {
            this.dismiss.countDown = dismissCountDown;
        },
        /**
         * Confirmar la eliminación de la imagen
         *
         * @param {Image} image     La imagen a eliminar
         *
         */
        confirmDeleteImage: function (image) {

            // Selecciona la imagen para ser eliminada
            image.deleting = true;

            // Abre la modal para confirmar la eliminación de la imagen
            this.$bvModal.show('modal-confirm-delete-image');

        },
        /**
         * Elimina la imagen seleccionada
         *
         */
        deleteImage: function () {

            // Obtiene la imagen a eliminar
            const image = this.images.filter(image => image.deleting)[0];

            // Elimina la imagen del servidor
            Axios.post(`/com/mount/remove/image/${image.id}`)
                .then(response => {

                     // Elimina la imagen de la lista de archivos subidos
                    this.files = this.files.filter(file => file.image != image);

                    // Elimina la imagen de la lista
                    this.images = this.images.filter(_image => _image != image);

                    // Oculta la modal para confirmar la eliminación de la imagen
                    this.$bvModal.hide('modal-confirm-delete-image');

                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Elimina de forma inmediata una imagen recién subida
         *
         * @param {Image} image     La imagen
         *
         */
        deleteUploadedImage: function (image) {
            // Marca la imagen para su eliminación
            image.deleting = true;

            // Elimina la imagen
            this.deleteImage();
        },
        /**
         * Cancela la eliminación de una imagen
         *
         */
        cancelDeleteImage: function () {
            // Desmarca como eliminable cualquier imagen
            this.images.forEach(image => image.deleting = false);

            // Cierra la modal
            this.$bvModal.hide('modal-confirm-delete-image');
        },
        /**
         * Devuelve la visibilidad de la imagen
         *
         * @param {Image} image         La imagen
         *
         * @return {String}             Una clase 'd-none'|'d-opacity' según la visibilidad de la imagen
         *
         */
        imageVisibility: function (image) {
            if (image.hidden && !this.userCanEditOrDeleteImage()) {
                return 'd-none';
            } else if (image.hidden && this.userCanEditOrDeleteImage()) {
                return 'd-opacity';
            } else {
                return '';
            }
        },
        /**
         * Edita la imagen
         *
         * @param {Number} image
         *
         */
        editImage: function (image) {

            // La imagen actualmente seleccionada para la edición de su información
            this.currentImage = image;

            // Abrir la modal para cambiar los datos de la imagen
            this.$bvModal.show('modal-edit-image');
        },
        /**
         * Muestra la orometría
         *
         */
        showOrometry: function () {

            let orometries = document.querySelectorAll('.orometry');

            orometries.forEach(orometry => {
                orometry.classList.remove('d-block');
                orometry.classList.add('d-none');
            });

            document.querySelector(`.orometry[data-id="${this.orometry}"]`).classList.add('d-block');
        },
       /**
         * Al seleccionar un archivo de imagen
         *
         * @param {File} newFile      El archivo nuevo          [Lectura y Escritura]
         * @param {File} oldFile      El archivo antiguo        [Sólo Lectura]
         */
        inputImage: function (newFile, oldFile) {

            if (!newFile) return;

            // Subida automática de los archivos seleccionados
            // Cuando el archivo es cargado por vez primera (al inicio de la subida)
            if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
                // Si la subida no está aún activa
                if (!this.$refs.upload.active) {

                   // Si el tamaño del archivo excede el tamaño admisible
                    if (newFile.file.size > this.upload.maxFileSize) {
                        newFile.error = 'abort';
                        return false;
                    }

                    // Marca la subida como activa
                    this.$refs.upload.active = true;
                }
            }
        },
        /**
         * Al filtrar los archivos de imágenes
         *
         * @param {File}     newFile   El archivo nuevo         [Lectura y Escritura]
         * @param {File}     oldFile   El archivo viejo         [Sólo Lectura]
         * @param {Function} prevent   Función que evita que la lista de archivos subidos sea cambiada
         *
         */
        inputFilterImage: async function (newFile, oldFile, prevent) {

            if (!newFile) return;

            // Si es un archivo nuevo y no uno viejo
            if (!oldFile) {
                // Comprueba que el archivo es una imagen válida
                if (!this.upload.imagesAcceptedTypes.includes(newFile.type)) {
                    // Muestra el mensaje de error, que el archivo no es válido para ser subido
                    this.$bvModal.show('file-image-not-valid');
                    // Evitar la actualización en la lista de archivos
                    return prevent();
                }
            }

            // El archivo ha sido subido con éxito
            if (newFile.success) {

                // Obtiene la imagen subida y completa su información
                let image = newFile.response;
                this.completeImage(image);

                // Añade la imagen al archivo subido
                // lo que permite poder eliminar el archivo o editarlo desde la tabla de archivos subidos
                newFile.image = image;

                // Para, finalmente, añadirla a la lista de imágenes
                this.images.push(image);
            }

            // Crea un objeto Blob
            newFile.blob = URL.createObjectURL(newFile.file);

            // Obtiene una miniatura de la imagen
            newFile.thumb = await this.resizeImage(newFile.blob, 100, 100);

        },
        /**
         * Redimensiona una imagen
         *
         * @param {String} data             Los datos de la imagen en formato Base64
         * @param {Number} wantedWidth      El ancho de la imagen requerido en píxeles
         * @param {Number} wantedHeight     El alto de la imagen requerido en píxeles
         *                                  Si no se especifica, se calcula un alto proporcional
         *                                  al ancho de la imagne original
         *
         * @return {Promise}                Una promesa con la imagen redimensionada en Base64
         *
         */
        resizeImage: function (data, wantedWidth, wantedHeight) {

            return new Promise(resolve => {

                // Creamos una nueva Imagen
                let image = new Image();

                // y cargamos a la imagen a redimensionar
                image.src = data;

                // Cuando la imagen está cargada
                image.onload = () =>
                    {
                        // Si no se ha proporcionado una altura, la obtiene en relación a la anchura
                        if (typeof wantedHeight === 'undefined') {
                            wantedHeight = wantedWidth * image.height / image.width
                        }

                        // Creamos un nuevo elemento canvas para efectuar la operación
                        let canvas = document.createElement('canvas');
                        let ctx    = canvas.getContext('2d');

                        // y lo dimensionamos con las dimensiones requeridas
                        canvas.width  = wantedWidth;
                        canvas.height = wantedHeight;

                        // Dibujamos la imagen
                        ctx.drawImage(image, 0, 0, wantedWidth, wantedHeight);

                        // Devolvemos la imagen redimensionada
                        resolve(canvas.toDataURL());
                    };
            });
        },
        /**
         * Al iniciar el movimiento de la imagen
         *
         * @param {Event} event
         *
         */
        onStartMoveImage: function (event) {
            // Obtiene el índice del elemento movido (from)
            // El primer es elemento es el que posee índice nulo
            const from = event.oldIndex;

            // La imagen que ha sido movida
            const image = this.images[from];

            // Si el usuario no tiene permiso para editar o eliminar la imagen
            // tampoco posee permiso para moverla
            if (!this.userCanEditOrDeleteImage(image)) {
                return false;
            }
        },
        /**
         * Al finalizar de mover una imagen
         *
         * @param {Event} event
         *
         */
        onEndMoveImage: function (event) {
            // Obtiene el índice del elemento movido (from)
            // El primer es elemento es el que posee índice nulo
            const from = event.oldIndex;
            // Obtiene el nuevo índice (lugar) del elemento que ha sido movido (to)
            const to   = event.newIndex;

            // La imagen que ha sido movida
            const image = this.images[from];

            if (!this.userCanEditOrDeleteImage(image)) {
                console.warn(`Operación no permitida para el usuario actual`);
                return false;
            }

            //
            // Bloqueamos la interfaz mientras se produce el nuevo ordenamiento de imágenes
            // actuando sobre la galería de imágenes
            //
            document.querySelector('.image-gallery').style.pointerEvents = 'none';

            // Construye la lista de ids de las imágenes tras el movimiento
            const images = this.images.map(image => image.id)

            // Envía la lista de imágenes según su posición actual
            Axios.post(`/com/mount/move/image/${image.id}`,
                {
                    images: images    // La lista de imágenes tras la ordenación
                }
            )
            .then(() => {
                console.info(`La imagen se ha movido con éxito de la posición ${from} a la posición ${to}`);
                // Recupera el control sobre la galería de imágenes
                document.querySelector('.image-gallery').style.pointerEvents = 'auto';
            })
            .catch(() => {
                console.warn(`Operación no permitida para el usuario actual`);
                // Recupera el control sobre la galería de imágenes
                document.querySelector('.image-gallery').style.pointerEvents = 'auto';
            });
        },
        /**
         * Guarda la imagen
         *
         * @param {Image} Image     Una imagen
         *
         */
        saveImage: function (image) {
            Axios.post(`/com/mount/edit/image/${image.id}`,
                {
                    user_id     : image.user_id,
                    taken_at    : image.taken_at,
                    title       : image.title,
                    hidden      : image.hidden,
                }
            )
            .then(() => {

                // Actualiza la información de la imagen
                this.completeImage(image);

                // Tiempo para cerrar la alerta que muestra que los
                // datos de la imagen se han guardado con éxito
                this.dismiss.countDown = this.dismiss.time;
            })
            .catch(error => {
                console.error(error);
            });
        },
        /**
         * Busca un usuario
         *
         */
        searchUser: function () {
            Axios.post(`/com/user/search`,
                {
                    search : this.searched.user,        // El nombre, apellidos, email del usuario a buscar
                }
            )
            .then(response => {
                this.searched.users = response.data;
            })
            .catch(error => {
                console.error(error);
            });
        },
        /**
         * Abre la modal de selección de usuario
         *
         * @param {Callback} userSelect     El método a ejecutar cuando se ha seleccionado el usuario
         *                                  Una vez se ha procedido a seleccionar un usuario, se ejecuta el método indicado
         *
         */
        userModalOpen: function (userSelect) {

            // Fija la función de callback cuando se ha seleccionado el usuario
            this.userSelect = userSelect;

            // Abre la modal de selección del usuario
            this.$bvModal.show('modal-select-user');

        },
        /**
         * Cuando se ha seleccionado un usuario en la modal de selección de usuarios
         * Se cierra la modal y se ejecuta el método de callback
         *
         * @param {User} user       El usuario
         */
        userModalSelect: function (user) {

            // Cierra la modal de selección del usuario
            this.$bvModal.hide('modal-select-user');

            // Ejecuta la función de callback que contiene al usuario seleccionado
            this.userSelect(user);
        },
        /**
         * Selecciona un usuario como autor de la montaña
         *
         * @param {User} user       Un usuario
         *
         */
        selectUserAsMountainAuthor: function (user) {

            // Añade el usuario a la lista de autores
            this.authors.push(
                {
                    id          : user.id,
                    name        : user.name,
                    lastname    : user.lastname,
                    thumb       : user.thumb,
                    username    : user.username,
                }
            );
        },
       /**
         * Selecciona un usuario como autor de la imagen actual
         *
         * @param {User} user       Un usuario
         *
         */
        selectUserAsImageAuthor: function (user) {

            // Fija la autoría en la imagen seleccionada actualmente
            this.currentImage.user_id     = user.id;
            this.currentImage.author      = `${user.name} ${user.lastname}`;
            this.currentImage.description = `${this.currentImage.author} :: ${moment(this.currentImage.taken_at ? this.currentImage.taken_at : this.currentImage.created_at).format('DD-MM-YYYY')}`;
            this.currentImage.userImage   = user.thumb;
        },
        /**
         * Edita la montaña
         *
         */
        editMount: function () {
            // Si la cumbre no tiene aún autoría, se añade la del usuario actual
            if (!this.hasAuthors) {
                // Elimina el usuario anónimo
                this.authors = [];
                // Añade al usuario actual como nuevo autor
                this.selectUserAsMountainAuthor(this.user);
            }

            // Fija el modo edición
            this.setMode('edit');
        },
        /**
         * Guarda la información de la montaña
         *
         */
        saveMount: function () {

            // Elimina cualquier error anterior
            for (let error in this.errors) {
                this.errors[error] = false;
            }

            // Obtiene la descripción (desde el editor tinymce)
            this.mountain.description = tinymce.get('description').getContent();

            // Descompone la posición UTM suministrada en sus tres partes constituyentes
            let positionParts     = this.mountain.utmZone = this.position.split(' ');
            this.mountain.datum   = this.datum;
            this.mountain.utmZone = positionParts[0];
            this.mountain.utmX    = positionParts[1];
            this.mountain.utmY    = positionParts[2];

            // Muestra el loader
            waiting.show();

            // Guarda la información de la montaña
            Axios.post('/com/mount/save',
                    {
                        mountain  : this.mountain,
                        authors   : this.authors,
                    }
                ).then(response => {

                    // Fija la información actualizada de la montaña
                    const data = response.data;

                    if (data && data.mountain) {
                        this.setMountain(
                            data.mountain,          // La información de la montaña
                            data.positions,         // Las posiciones en diferentes datums
                            data.nearests           // Las cumbres próximas
                        );

                        // Actualiza la posición de las cumbres
                        // Permite recolocar la montaña si su posición se ha modificado
                        this.updateSummits();
                    }

                    // Pasa al modo visor
                    this.setMode('view');

                    // Oculta el loader
                    waiting.hide();

                    // Muestra la modal indicando que la información de la montaña se ha guardado con éxito
                    this.$bvModal.show('modal-save-success');
                })
                .catch(error => {

                    // Oculta el loader
                    waiting.hide();

                     // Marca los errores en la información suministrada
                    for (error in  error.response.data.errors) {
                        this.errors[error.replace('mountain.', '')] = true;
                    }

                    // Muestra la modal indicando que la información proporcionada no es correcta
                    this.$bvModal.show('modal-save-failed');

                });
        },
        /**
         * Fija el modo de visualización de la información
         *
         * @param {String} mode     El modo: 'view'|'edit' (visor|editor)
         *
         */
        setMode: function (mode) {

            // Guarda la referencia a la instancia de Vue
            const $this = this;

            this.mode = mode;

            // Habilita/Deshabilita el editor tinymce
            if (this.mode == 'edit') {
                // Elimina los anuncios de Adsense
                document.querySelectorAll('.adsbygoogle')
                        .forEach(ad => ad.remove());

                // Muestra el loader
                waiting.show();

                // Obtiene la descripción de la cumbre sin procesar
                Axios.post(`/com/mount/no-processed/description/${this.mountain.id}`)
                    .then(response => {
                        // Obtenemos la descripción sin procesar

                        this.mountain.description = response.data.description;

                        // Oculta el loader
                        waiting.hide();

                        // Preparamos el editor de la descripción
                        tinymce.init({
                            selector        : '#description',
                            language        : this.lang,
                            language_url    : `/assets/vendor/tinymce/langs/${this.lang}.js`,
                            browser_spellcheck: true,
                            plugins         : 'link autolink image lists media table code',
                            contextmenu     : 'copy cut paste | link image',
                            toolbar         : 'undo redo | removeformat | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table filebrowser code',
                            toolbar_mode    : 'scrolling',
                            statusbar       : false,
                            inline          : true,
                            menubar         : false,
                            paste_as_text   : true,
                            paste_text_sticky : true,
                            toolbar_sticky  : true,
                            init_instance_callback: function (editor) {
                                // Fija el foco al iniciar y se muestra la barra de herramientas
                                editor.fire('focus');
                            },
                            setup: function (editor) {
                                // Previene que el evento blur oculte la barra de herramientas
                                editor.on('blur', function () {
                                    return false;
                                });

                                // Botón de administrador de archivos subidos
                                // Abre la lista de archivos pdf subidos al servidor para seleccionar, subir o administrar alguno de ellos
                                editor.ui.registry.addButton('filebrowser', {
                                    icon: 'browse',
                                    onAction: function () {
                                        // Carga la lista de documentos en el
                                        //editor (tinymce) actual con el que interactua el visor de deocumentos
                                        $this.loadFileBrowserDocuments(editor);
                                    }
                                });

                            },
                        });

                        // Mueve la posición hasta la descripción para su edición
                        this.$refs.description.scrollIntoView();
                    })
                    .catch(error => {

                        // Oculta el loader
                        waiting.hide();

                        console.error(error);
                    });
            } else {
                tinymce.get('description').remove();
            }
        },
        /**
         * Añade una región a la montaña
         *
         */
        addRegion: function () {
            // Si la región ya estaba incluída en la lista de regiones seleccionadas se omite
            if (this.mountain.regions.map(region => region.name).includes(this.region.name)) return;

            // Añade la región
            this.mountain.regions.push(this.region);
        },
        /**
         * Elimina una región de la montaña
         *
         * @param {Region} region       El territorio a eliminar
         *
         */
        removeRegion: function (region) {
            // La montaña debe pertenecer, al menos, a una región
            if (this.mountain.regions.length == 1) return;

            this.mountain.regions = this.mountain.regions.filter(_region => _region != region);
        },
        /**
         * Añade la montaña a un catálogo
         *
         */
        addCatalog: function () {
            // Si la montaña ya estaba incluída en el catálogo se omite
            if (this.mountain.catalogs.map(catalog => catalog.name).includes(this.catalog.name)) return;

            // Añade el catálogo
            this.mountain.catalogs.push(this.catalog);
        },
        /**
         * Elimina la montaña de un catálogo
         *
         * @param {Catalog} catalog         El catálogo a eliminar
         *
         */
        removeCatalog: function (catalog) {
            this.mountain.catalogs = this.mountain.catalogs.filter(_catalog => _catalog != catalog);
        },
        /**
         * Añade un nuevo autor a la montaña
         *
         */
        addAuthor: function () {
            // Abre la modal de selección de usuarios para seleccionar un nuevo autor para la montaña
            this.userModalOpen(this.selectUserAsMountainAuthor);
        },
        /**
         * Cambia la autoría de una imagen
         *
         */
        changeImageAuthor: function () {
            // Abre la modal de selección de usuarios para seleccionar un nuevo autor para la imagen
            this.userModalOpen(this.selectUserAsImageAuthor);
        },
        /**
         * Elimina un autor
         *
         * @param {Author} author       El autor
         *
         */
        removeAuthor: function (author) {
            this.authors = this.authors.filter(_author => _author != author);
        },
        /**
         * Carga un track para ser subido
         *
         */
        loadTrack: function () {
            // Abre el explorador de archivos para seleccionar un track
            trackFile.click();
        },
        /**
         * Sube el archivo de track
         *
         * @param {Event} event
         *
         */
        uploadTrack: function(event) {

            let formData = new FormData();

            formData.append('track', event.target.files[0]);

            Axios.post(`/com/mount/upload/track/${this.mountain.id}`,
                formData,
                {
                    headers:
                        {
                            'Content-Type': 'multipart/form-data',
                        },
                        onUploadProgress: function(progressEvent) {
                            this.upload.progress = parseInt( Math.round( ( progressEvent.loaded / progressEvent.total ) * 100 ));
                        }.bind(this)
                },
            )
            .then(response => {
                // Obtiene la información del track
                let track = response.data;

                if (typeof track.id === 'undefined') {
                    // Si la respuesta no devuelve un track
                    this.errors.track.message = null;

                    // Vuelve la barra de progreso de subida de archivo a cero
                    this.upload.progress = 0;

                    // Abre la modal mostrando el error
                    this.$bvModal.show('file-track-not-valid');
                } else {
                    // Redirige a la página del track
                    location.href = `/com/track/${track.id}`;
                }
            })
            .catch(error => {

                // Obtiene el mensaje de error si existe
                if (error.response) {
                    this.errors.track.message = error.response.data.message;
                }

                // Vuelve la barra de progreso de subida de archivo a cero
                this.upload.progress = 0;

                // Abre la modal mostrando el error
                this.$bvModal.show('file-track-not-valid');
            });
        },
        /**
         * Si el usuario actual puede editar o eliminar la imagen
         *
         * @param {Image} image     Una imagen
         *
         * @returns {Boolean}
         *
         */
        userCanEditOrDeleteImage: function (image) {
            // Si el usuario es administrador o es el autor de la imagen
            return this.userIsAdmin || (this.user != null && image != null && this.user.id == image.user_id);
        },
        /**
         * Si el usuario actual puede editar o eliminar un comentario
         *
         * @param {Post} post       Un mensaje o comentario
         *
         * @returns {Boolean}
         *
         */
        userCanEditOrDeletePost: function (post) {
            // Si el usuario es administrador o es el autor del comentario o post
            return this.userIsAdmin || (this.user && post && this.user.id == post.user_id);
        },
        /**
         * Descarga un mapa
         *
         * @param {Number} map      El id dl mapa
         *
         */
        downloadMap: function (map) {
            location.href = `/com/map/download/${map}`;
        },
        /**
         * Abre el visor de cartografía digital
         *
         *
         */
        openMapViewer: function () {

            // Selecciona el visor de mapas
            this.viewer = this.viewers[0];

            // Abrir la modal para mostrar el visor
            this.$bvModal.show('modal-map-viewer');
        },
        /**
         * Descarga la información en un archivo Gogle Earth
         *
         */
        downloadGoogleEarth: function () {
            location.href = `/com/mount/download/google-earth/${this.mountain.id}`;
        },
        /**
         * Obtiene las iniciales del usuario autor de un comentario
         *
         * @param {String} author   Un autor
         *
         * @returns {String}        Las iniciales del usuario
         *
         */
        getAuthorInitials: function (author) {
            let regex= new RegExp(/(\p{L}{1})\p{L}+/, 'gu');

            let initials = [...author.matchAll(regex)] || [];

            return (
              (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
            ).toUpperCase();
        },
        /**
         * Crea un nuevo mensaje o post
         *
         */
        createPost: function () {

            // Crea el post
            const post =
                {
                    id          : 0,
                    user_id     : this.user.id,
                    created_at  : new Date(),
                    author      : `${this.user.name} ${this.user.lastname}`,
                    fixed       : false,
                    message     : null,
                    image       : this.user.thumb,
                    editable    : true,
                };

            this.posts.unshift(post);

            // Re-renderizar la instancia
            vm.$forceUpdate();

            // Edita el nuevo mensaje
            setTimeout(()=> {this.editPost(post);}, 1000);
        },
        /**
         * Guarda el comentario o post
         *
         * @param {Post} post
         *
         */
        savePost: function (post) {

            // Guarda el contenido del post
            post.message = tinymce.get(`post${post.id}`).getContent();

            // Si no hay mensaje no se permite guardar
            if (!post.message) {
                tinymce.get(`post${post.id}`).focus();
                return;
            }

            // Elimina el editor
            tinymce.get(`post${post.id}`).remove();

            post.editing = false;

            if (post.id) {
                // Edición de un post ya existente
                Axios.post(`/com/mount/post/edit`, {
                    post : post,
                })
                .then(response => {
                    post.message = response.data.message;

                    // Comentario guardado con éxito
                    this.$bvModal.show('modal-post-saved-success');
                })
                .catch(error => {
                    console.error(error);
                });
            } else {
                // Creación de un nuevo post
                Axios.post(`/com/mount/post/create/${this.mountain.id}`, {
                    post : post,
                })
                .then(response => {
                    // Fija el id del nuevo comentario o post creado
                    post.id = response.data.id;
                    // Fija el contenido del comentario procesado
                    post.message = response.data.message;

                    // Muestra la modal que se ha guardado el post con éxito
                    this.$bvModal.show('modal-post-saved-success');
                })
                .catch(error => {
                    console.error(error);
                });
            }

            // Re-renderizar la instancia
            vm.$forceUpdate();
        },
        /**
         * Cancela la edición del post sin guardar
         *
         * @param {Post} post
         *
         */
        cancelPost: function (post) {

            // Elimina el editor
            tinymce.get(`post${post.id}`).remove();

            // Marca el comentario como no editado
            post.editing = false;

            // Re-renderizar la instancia
            vm.$forceUpdate();
        },
        /**
         * Confirma la eliminación de un comentario o post
         *
         * @param {Post} post
         *
         */
        confirmDeletePost: function (post) {

            // Marca el post como eliminable
            post.deleting = true;

            // Abrir la modal para confirmar la eliminación del comentario
            this.$bvModal.show('modal-confirm-delete-post');
        },
        /**
         * Cancela la eliminación de un comentario
         *
         */
        cancelDeletePost: function () {
            // Desmarca como eliminable cualquier comentario
            this.posts.forEach(post => post.deleting = false);

            // Cierra la modal
            this.$bvModal.hide('modal-confirm-delete-post');
        },
        /**
         * Elimina el comentario o post
         *
         */
        deletePost: function () {

            // Obtiene el post a eliminar
            const post = this.posts.filter(post => post.deleting)[0];

            // Eliminación de un post
            Axios.post(`/com/mount/post/delete`, {
                post : post,
            })
            .then(() => {
                // Elimina el post del conjunto de posts
                this.posts = this.posts.filter(_post => _post != post);
                // Cierra la modal
                this.$bvModal.hide('modal-confirm-delete-post');
            })
            .catch(error => {
                console.error(error);
            });
        },
        /**
         * Edita el comentario o post
         *
         * @param {Post} post
         *
         */
        editPost: async function (post) {

            // Si el mensaje no es nuevo (post.id no es nulo)
            // Obtiene el mensaje sin procesar
            if (post.id != 0) {

                // Muestra el loader
                waiting.show();

                await Axios.post(`/com/mount/no-processed/message/${post.id}`)
                    .then(response => {

                        // Oculta el loader
                        waiting.hide();

                        // Obtenemos el mensaje sin procesar
                        post.message = response.data.message;

                    });
            }

            tinymce.init({
                selector        : `#post${post.id}`,
                language        : this.lang,
                language_url    : `/assets/vendor/tinymce/langs/${this.lang}.js`,
                plugins         : 'link autolink image media lists table code',
                toolbar         : 'undo redo | removeformat | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table code',
                toolbar_mode    : 'scrolling',
                statusbar       : false,
                inline          : true,
                menubar         : false,
                paste_as_text   : true,
                toolbar_sticky  : true,
                init_instance_callback: function (editor) {
                    // Fija el foco al iniciar y se muestra la barra de herramientas
                    editor.fire('focus');
                },
                setup: function (editor) {
                    // Previene que el evento blur oculte la barra de herramientas
                    editor.on('blur', function () {
                        return false;
                    });
                },
            });

            post.editing = true;

            // Re-renderizar la instancia
            vm.$forceUpdate();


        },
        /**
         * Comprueba si el post está siendo editado o no
         *
         * @param {Post} post
         *
         */
        postIsEditing: function  (post) {
            return post.editing;
        },
        /**
         * Crea una montaña nueva
         *
         */
        createMountain: function () {
            location.href = '/com/mount/create/new';
        },
        /**
         * Confirma la eleminación de la montaña
         *
         */
        confirmDeleteMountain: function () {
            this.$bvModal.show('modal-confirm-delete-mountain');
        },
        /**
         * Elimina una montaña
         *
         */
        deleteMountain: function () {
            Axios.post(`/com/mount/delete/${this.mountain.id}`)
                .then(() => {
                    // Oculta la modal de confirmación de la eliminación
                    // y muestra la modal que indica que la montaña ha sido eliminada
                    this.$bvModal.hide('modal-confirm-delete-mountain');
                    this.$bvModal.show('modal-delete-mountain-confirmed');
                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Al cerrar la modal de ascensiones
         *
         */
        hideClimbsModal: function () {
            // No hacer nada
        },
        /**
         * Muestra la información del autor
         *
         * @param {Author} author
         *
         */
        showAuthorInfo: function (author) {

            // Muestra el loader
            waiting.show();

            // Obtiene la información del autor
            Axios.post(`/com/user/author/info/${author.id}`)
                .then(response => {

                    // Oculta el loader
                    waiting.hide();

                    // La información del autor
                    this.authorInfo = response.data;

                    // Oculta la modal de confirmación de la eliminación
                    this.$bvModal.show('modal-author-info');
                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Abre la modal con la información de funcionamiento de la página
         * útil apara nuevos usuarios
         *
         * @returns {Boolean}       true si se abre la modal, false si no es necesario
         *
         */
        showHowToUseModalForNewUsers: function () {

            // La modal no está disponible para usuarios no registrados
            if (!this.user) return false;

             // Si la modal de uso de la página debe ser abierta o no
            if (localStorage.getItem('modal.howToUse.mount') == null) {
                this.howToUse = false;
                localStorage.setItem('modal.howToUse.mount', this.howToUse);
            } else {
                this.howToUse = localStorage.getItem('modal.howToUse.mount') === 'true';
            }

            // Abre la modal con las instrucciones
            if (!this.howToUse) {
                this.$bvModal.show('modal-info-how-to-use-mount');
            }

            return !this.howToUse;
        },
        /**
         * Guarda el estado de mostrado de la ventana de información de uso de la página
         *
         */
        saveHowToUse: function () {
            localStorage.setItem('modal.howToUse.mount', !this.howToUse);
        },
        /**
         * Comparte la página actual de información de la cumbre
         *
         * @param {Mountain} mountain
         */
        shareMountain: async function (mountain) {
            // Obtener la URL y la información de la cumbre a compartir
            const fullUrl = `${window.location.protocol}//${window.location.hostname}${mountain.url}`;
            const name = this.$options.filters.name(mountain.name);
            const altitude = this.$options.filters.altitude(mountain.altitude);

            try {
                await navigator.share(
                    {
                        title: `${name} (${altitude})`,
                        text: `${name} (${altitude})`,
                        url: fullUrl
                    }
                );
                console.info(`Se ha compartido la url de la cumbre ${fullUrl}`);
            } catch (error) {
                // Copia la url en el portapapeles
                navigator.clipboard.writeText(fullUrl)
                    .then(() => {
                        this.linkCopied = true;
                        console.info(`Se ha copiado la url de la cumbre ${fullUrl} en el portapapeles`);
                    })
                    .catch(error => {
                        console.error(error);
                    });
            }

        },
        /**
         *
         * @param {Event}
         */
        handleCopy: function (event) {
            // En modo edición sale
            if ('edit' === this.mode) {
                return true;
            }

            // Evita la acción predeterminada
            event.preventDefault();

            // Referencia al portapapeles
            const clipboard = event.clipboardData || window.clipboardData;

            // Accede al contenido del mensaje
            const copyHandlerMessage = this.$refs.copyHandlerMessage.innerText;

            clipboard.setData(
                'text/plain',
                copyHandlerMessage
            );

            // Registra el contenido copiado
            Axios.post('/com/user/copied/content/register',
                {
                    url: location.href,
                }
            ).then(() => {
                console.info(copyHandlerMessage);
            }).catch(error => {
                console.error(error);
            });
        },
    },
    computed: {
        /**
         * Si el agente de usuario actual es un dispositivo móvil o no
         *
         * @returns {Boolean}
         *
         */
        isMobile: function() {
            return isMobile;
        },
        /**
         * Si la información es editable o no
         *
         * @returns {Boolean}
         *
         */
        editable: function () {
            return this.mode == 'edit';
        },
        /**
         * Si la información no es editable o si
         *
         * @returns {Boolean}
         *
         */
        notEditable: function () {
            return this.mode == 'view';
        },
        /**
         * Cuando el mapa ha sido inicializado
         *
         * @returns {Boolean}       true si el mapa ha sido inicializado
         */
        mapInitialized: function () {
            return this.map.provider != null;
        },
        /**
         * Obtiene los tracks filtrados en función del texto de búsqueda suministrado en el filtro
         *
         * @returns {Array}         Una lista de tracks filtrados
         *
         */
        filteredTracks: function () {
            if (!this.filter)  {
                return this.tracks;
            } else  {
                return this.tracks.filter(track => {
                    return track.description.toLowerCase().includes(this.filter.toLowerCase());
                });
            }
        },
        /**
         * Muestra la posición en coordenadas geográficas
         *
         * @returns {String}
         */
        showGeoPosition: function () {
            return this.mountain.positions[this.datum].geo.value;
        },
        /**
         * Muestra la proyección UTM o Lambert
         *
         * @returns {String}
         */
        showUtmProjection: function () {
            return this.mountain.positions[this.datum].utm.projection;
        },
        /**
         * Muestra la proyección en corrdenadas elipsoidales (geográficas)
         *
         * @returns {String}
         */
        showGeoProjection: function () {
            return this.mountain.positions[this.datum].geo.projection;
        },
       /**
         * Si el usuario actual puede editar o eliminar la imagen
         *
         * @returns {Boolean}
         *
         */
        userCanEditOrDeleteMount: function () {
            // Si el usuario es administrador o es el autor único de la reseña
            return this.user && (this.userIsAdmin || this.userIsExclusiveAuthor);
        },
        /**
         * Si el usuario actual es administrador o no
         *
         * @returns {Boolean}
         *
         */
        userIsAdmin: function () {
            return this.user != null && (this.user.privileges == 1 || this.user.privileges == 2);
        },
        /**
         * Si el usuario actual es autor exclusivo de la montaña o no
         *
         * @returns {Boolean}
         */
        userIsExclusiveAuthor: function () {
            return this.authors.length == 1 && this.authors[0].id == this.user.id;
        },
        /**
         * Comprueba si un nuevo comentario o post ya ha sido creado
         *
         * @returns {Boolean}
         *
         */
        newPostAlreadyCreated: function () {
            // Si se ha creado ya un comentario, no se permite crear otro nuevo
            // El post ya creado tiene id nulo
            return this.posts.filter(post => post.id == 0).length != 0;
        },
        /**
         * Comprueba si la cumbre posee un autor o no
         * La cumbre sin autor está dada de alta por el usuario anónimo
         *
         * @returns {Boolean}
         *
         */
        hasAuthors: function () {
            // El id del usuario anónimo (Anonymous) es 1
            return !(this.authors.length == 0 || this.authors.length == 1 && this.authors[0].id == 1);
        },
    },
    filters: {
        /**
         * Formatea el nombre de una cumbre
         *
         * @param {String} name         El nombre de la cumbre
         *
         */
        name: function (name) {
            return name.split('/')[0];
        },
        /**
         * Formatea un valor de altitud
         *
         * @param {Number} value        Un valor de altitud
         *
         * @returns {String}            La altitud formateada
         *
         */
        altitude: function (value) {
            return `${parseInt(value)} m`;
        },
         /**
         * Formatea un valor de distancia
         *
         * @param {Number} value        Un valor de distancia
         * @returns {String}            La distancia formateada
         *
         */
        distance: function (value) {
            return `${numeral(value / 1000).format('0.000').replace('.', ',')} km`;
        },
        /**
         * Formatea una fecha
         *
         * @param {Date} value          Una fecha
         * @param [String} type         Representación de la fecha en formato largo o corto
         *                                  'short'|'long'
         * @returns {String}            Una fecha formateada
         */
        date: function (value, type = 'long') {
            if (type == 'long') {
                return __MOUNT__.lang == 'eu' ?
                    moment(value).locale(__MOUNT__.lang).format('YYYY[-(e)ko] MMMM[k] D')
                                        :
                    moment(value).locale(__MOUNT__.lang).format('D MMMM YYYY');
            } else {
                return __MOUNT__.lang == 'eu' ?
                    moment(value).format('YYYY-MM-DD')
                                    :
                    moment(value).format('DD-MM-YYYY');
            }
        },
        /**
         * Formatea un tamaño de archivo
         *
         * @param {Number} value        El tamaño de archivo en bytes
         *
         * @returns {String}            El tamaño de archivo formateado
         *
         */
        filesize: function (value) {
            return value < 1024 * 1024 ? `${numeral(value/1024).format('0')} kB` : `${numeral(value/1024/1024).format('0.0').replace('.', ',')} MB`;
        },
    }
 });
